import { apiClient } from './client';
import { MEDIA_QUERIES } from './graphql/queries/media';
import type { MediaInferenceData, MediaPersonaData } from './persona-service';

// ===== GraphQL Response Types =====

export type MediaInputData = {
  id: number;
  name: string;
  extension: string;
  url: string;
  urlThumbnail?: string;
};

export type MediaInputEdge = {
  cursor: string;
  node: MediaInputData;
};

export type MediaInferenceEdge = {
  cursor: string;
  node: MediaInferenceData;
};

export type PageInfo = {
  nextCursor: string | null;
  hasNextPage: boolean;
};

export type MediaInputsResponse = {
  data: {
    persona: {
      id: number;
      mediaInputs?: {
        pageInfo: PageInfo;
        edges: MediaInputEdge[];
      };
    };
  };
};

export type MediaInferencesResponse = {
  data: {
    mediaInferences: {
      pageInfo: PageInfo;
      edges: MediaInferenceEdge[];
    };
  };
};

export type MediaPersonaResponse = {
  data: {
    mediaPersona: MediaPersonaData;
  };
};

export type MediaInferenceResponse = {
  data: {
    mediaInference: MediaInferenceData;
  };
};

export type InferenceData = {
  id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  personaId: number;
  provider?: string;
  inputPrompt?: string;
  mediaType?: 'image' | 'video' | 'audio';
  resolution?: string;
  duration?: number;
  cameraFixed?: boolean;
  mediaInference?: MediaInferenceData;
};

export type InferenceEdge = {
  cursor: string;
  node: InferenceData;
};

export type InferencesResponse = {
  data: {
    inferences: {
      pageInfo: PageInfo;
      edges: InferenceEdge[];
    };
  };
};

export type InferenceResponse = {
  data: {
    inference: InferenceData;
  };
};

// ===== Service Class =====

export class MediaGraphQLService {
  /**
   * Get MediaInputs for a persona (NSFW images uploaded via media-input API)
   * Uses mediaInferences query since mediaInputs field doesn't exist on Persona
   */
  static async getMediaInputs(
    personaId: number,
    limit: number = 50,
    isNSFW: boolean = true,
  ): Promise<MediaInputData[]> {
    try {
      const response = await this.getMediaInferences(personaId, limit, undefined, isNSFW);

      // Convert MediaInferences to MediaInputData format
      return response.data.mediaInferences.edges.map((edge) => ({
        id: edge.node.id,
        name: edge.node.name || `media_${edge.node.id}`,
        extension: edge.node.extension || 'png',
        url: edge.node.url,
        urlThumbnail: edge.node.urlThumbnail || '',
      }));
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get MediaPersonas for a persona using persona query with medias field
   */
  static async getMediaPersonas(personaId: number): Promise<MediaPersonaData[]> {
    try {
      const response = await apiClient.post('/graphql', {
        query: `
          query GetPersonaMedias($id: ID!) {
            persona(id: $id) {
              medias(limit: 50, exclude_inferences: true) {
                edges {
                  node {
                    id
                    name
                    originalName
                    url
                    urlThumbnail
                    extension
                    personaId
                    isInDataset
                  }
                }
                pageInfo {
                  hasNextPage
                  nextCursor
                }
              }
            }
          }
        `,
        variables: {
          id: personaId,
        },
      });

      // Extract nodes from edges structure
      const edges = response.data.data.persona?.medias?.edges || [];
      return edges.map((edge: { node: MediaPersonaData }) => edge.node);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get single MediaPersona by ID
   */
  static async getMediaPersona(id: number): Promise<MediaPersonaResponse> {
    try {
      const response = await apiClient.post<MediaPersonaResponse>('/graphql', {
        query: MEDIA_QUERIES.GET_MEDIA_PERSONA,
        variables: {
          id: id.toString(),
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get single MediaInference by ID
   */
  static async getMediaInference(id: number): Promise<MediaInferenceResponse> {
    try {
      const response = await apiClient.post<MediaInferenceResponse>('/graphql', {
        query: MEDIA_QUERIES.GET_MEDIA_INFERENCE,
        variables: {
          id: id.toString(),
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get MediaInferences directly (cleaner than getInferences)
   */
  static async getMediaInferences(
    personaId: number,
    limit: number = 50,
    cursor?: string,
    isNSFW?: boolean,
    mediaType?: 'image' | 'video' | 'audio',
  ): Promise<MediaInferencesResponse> {
    try {
      const response = await apiClient.post<MediaInferencesResponse>('/graphql', {
        query: MEDIA_QUERIES.GET_MEDIA_INFERENCES,
        variables: {
          personaId,
          limit,
          cursor,
          isNSFW,
          mediaType,
        },
      });

      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get paginated Inferences for a persona (not MediaInference, but actual Inference objects)
   * @deprecated Use getMediaInferences for direct MediaInference access
   */
  static async getInferences(
    personaId: number,
    limit: number = 50,
    cursor?: string,
    mediaType?: 'image' | 'video' | 'audio',
  ): Promise<InferencesResponse> {
    try {
      const response = await apiClient.post<InferencesResponse>('/graphql', {
        query: MEDIA_QUERIES.GET_INFERENCES,
        variables: {
          personaId,
          limit,
          cursor: cursor || null,
          mediaType,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get single Inference by ID
   */
  static async getInference(id: number): Promise<InferenceResponse> {
    try {
      const response = await apiClient.post<InferenceResponse>('/graphql', {
        query: MEDIA_QUERIES.GET_INFERENCE,
        variables: {
          id: id.toString(),
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get media with specified limit for Image Studio
   */
  static async getMedia(personaId: number, limit: number = 50): Promise<MediaInferenceData[]> {
    try {
      const mediaInferencesResponse = await this.getMediaInferences(personaId, limit);

      return mediaInferencesResponse.data.mediaInferences.edges.map((edge) => edge.node);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Extract dataset and output media for a persona using GraphQL
   */
  static async getPersonaMedia(personaId: number): Promise<{
    dataset: MediaPersonaData[];
  }> {
    try {
      // Fetch only dataset media - output media is handled by React Query hooks
      const personasData = await this.getMediaPersonas(personaId); // Get MediaPersona data from persona.medias

      return {
        dataset: personasData,
      };
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Update media inference displayName using PATCH endpoint
   */
  static async updateMediaInference(id: number, displayName: string): Promise<MediaInferenceData> {
    try {
      const response = await apiClient.patch<MediaInferenceData>(`/media-inference/${id}`, {
        displayName,
      });
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: unknown } }).response.data;
      }
      throw error;
    }
  }
}

export { MediaGraphQLService as mediaGraphQLService };
